import React, { useEffect, useState } from "react";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import { Info } from "lucide-react";
import { NavBarMain, Bookie } from "..";
import { useUser } from "../../hooks";
import {
  BookieContainer, BookieHeaderText,
  BookiesHeaderContainer, BookiesListContainer, PageContainer,
  SearchContainer, SearchInput, TabsContainer, TabButton,
  ProviderGroupContainer, ProviderGroupHeader, ProviderBookiesGrid, ProviderBadge,
  ProvidersGrid, SingleProviderContainer, ProviderTooltip, ProviderInfoIcon
} from './UserBookies.styled'
import { checkIfTokenStillValid } from "../../repositories/utils";
import Calculator from "./Calculator";
import { useTranslation } from "react-i18next";
import { useUsers } from "../../contexts/UsersContext";

export const UserBookies = ({ history, match }) => {
    const { t } = useTranslation();
    const { bookieAction, getBookies } = useUser()
    const { mutate } = useUsers();
    const [activeBookies, setActiveBookies] = useState({});
    const [bookies, setBookies] = useState(null);
    const [activeTab, setActiveTab] = useState("available");
    const [searchQuery, setSearchQuery] = useState("");

    const refreshBookies = async () => {
      const updatedBookies = await getBookies(match.params.email, false);
      setBookies(updatedBookies);
    };

    useEffect(() => {
      refreshBookies();
      checkIfTokenStillValid(history)
    }, [match.params.email, mutate])

    const setBookieStatus = (bookie, enabledStatus) => {
      setActiveBookies({...activeBookies, [bookie]: enabledStatus});
    };

    const toggleBookieStatus = async (bookie, userEnabled) => {
      const action = userEnabled ? "disable" : "enable";
      await bookieAction(match.params.email, bookie, action)
      const newStatus = action === "enable";
      setBookieStatus(bookie, newStatus);
      await refreshBookies();
    };

    const filterBookies = (bookies) => {
      if (!bookies) return [];
      
      return bookies
        .filter(bookie => activeTab === "available" ? !bookie.inUse : bookie.inUse)
        .filter(bookie => {
          const searchTerm = searchQuery.toLowerCase();
          return bookie.name.toLowerCase().includes(searchTerm) ||
                 (bookie.provider && bookie.provider.toLowerCase().includes(searchTerm));
        });
    };

    const groupBookiesByProvider = (bookies) => {
      const grouped = bookies.reduce((acc, bookie) => {
        const provider = bookie.provider || t('noProvider');
        if (!acc[provider]) {
          acc[provider] = [];
        }
        acc[provider].push(bookie);
        return acc;
      }, {});
      
      // Sort bookies within each group alphabetically
      Object.keys(grouped).forEach(provider => {
        grouped[provider].sort((a, b) => a.name.localeCompare(b.name));
      });
      
      return grouped;
    };

    const renderTabContent = () => {
      if (activeTab === "calculator") {
        return <Calculator email={match.params.email} />;
      }

      const filteredBookies = filterBookies(bookies);
      const groupedBookies = groupBookiesByProvider(filteredBookies);
      const providerKeys = Object.keys(groupedBookies).sort();
      
      // Separate single and multi-bookie providers
      const singleBookieProviders = providerKeys.filter(provider => groupedBookies[provider].length === 1);
      const multiBookieProviders = providerKeys.filter(provider => groupedBookies[provider].length > 1);

      const renderProviderGroup = (provider, isSingle = false) => {
        const Container = isSingle ? SingleProviderContainer : ProviderGroupContainer;
        const hasMultipleBookies = groupedBookies[provider].length > 1;
        
        return (
          <Container key={provider}>
            <ProviderGroupHeader>
              {provider}
              <ProviderBadge>
                {groupedBookies[provider].length} {groupedBookies[provider].length === 1 ? t('bookie') : t('bookies')}
              </ProviderBadge>
              {hasMultipleBookies && (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <ProviderTooltip id={`provider-tooltip-${provider}`}>
                      {t('multiBookieProviderTooltip')}
                    </ProviderTooltip>
                  }
                >
                  <ProviderInfoIcon>
                    <Info size={16} />
                  </ProviderInfoIcon>
                </OverlayTrigger>
              )}
            </ProviderGroupHeader>
            <ProviderBookiesGrid singleBookie={groupedBookies[provider].length === 1}>
              {groupedBookies[provider].map((bookie) => (
                <Bookie
                  key={bookie.id}
                  bookie={bookie}
                  toggleBookieStatus={toggleBookieStatus}
                  activeBookies={activeBookies}
                  bookies={bookies}
                  type={activeTab === "available" ? "add" : "edit"}
                  onRefresh={refreshBookies}
                />
              ))}
            </ProviderBookiesGrid>
          </Container>
        );
      };

      return (
        <BookieContainer>
          <BookiesHeaderContainer>
            <BookieHeaderText>
              {t(activeTab === "available" ? "availableBookies" : "bookiesInUse")}
            </BookieHeaderText>
            <SearchContainer>
              <SearchInput
                type="text"
                placeholder={t("searchBookies")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchContainer>
          </BookiesHeaderContainer>
          
          {/* Single bookie providers in grid */}
          {singleBookieProviders.length > 0 && (
            <ProvidersGrid>
              {singleBookieProviders.map(provider => renderProviderGroup(provider, true))}
            </ProvidersGrid>
          )}
          
          {/* Multi bookie providers full width */}
          {multiBookieProviders.map(provider => renderProviderGroup(provider, false))}
        </BookieContainer>
      );
    };

    return (
      <PageContainer>
        <NavBarMain currentPage="bothome" history={history}/>
        <TabsContainer>
          {["available", "inUse", "calculator"].map((tab) => (
            <TabButton
              key={tab}
              active={activeTab === tab}
              onClick={() => {
                setActiveTab(tab);
                setSearchQuery("");
              }}
            >
              {t(tab === "available" ? "availableBookies" : tab === "inUse" ? "bookiesInUse" : "calculator")}
            </TabButton>
          ))}
        </TabsContainer>
        {renderTabContent()}
      </PageContainer>
    );
  };
